import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { KeyboardEvent, ReactNode } from 'react';

interface DisabledActionWrapperProps {
  /**
   * When true, the wrapper intercepts clicks/keyboard activation and calls
   * `onTrigger` (typically to show a toast explaining why the wrapped control
   * is disabled). When false, the wrapper is an inert passthrough.
   */
  active: boolean;
  onTrigger: () => void;
  /** Describes the action for assistive tech while the wrapper is active. */
  ariaLabel: string;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/**
 * Wraps a disabled control (e.g. a disabled button) so that clicking or
 * keyboard-activating it still triggers feedback. A plain `Box` with `onClick`
 * is not keyboard-accessible nor announced by screen readers, so when `active`
 * this exposes `role="button"`, makes the element focusable, and handles
 * Enter/Space.
 */
export function DisabledActionWrapper({
  active,
  onTrigger,
  ariaLabel,
  sx,
  children,
}: DisabledActionWrapperProps) {
  if (!active) {
    return <Box sx={sx}>{children}</Box>;
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onTrigger();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onTrigger}
      onKeyDown={handleKeyDown}
      sx={sx}
    >
      {children}
    </Box>
  );
}
